import React from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import moment from "moment";

import { DishCategoryTagService } from "../../services/DishCategoryTagService";
import EventBus from "../../EventBus";
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";
import { CustomSnackbarTypes } from "../Common/CustomSnackBar";

const dishCategoryTagService = new DishCategoryTagService();

interface Props {
  name: string;
  iconUrl: string;
  onUploaded: (url: string) => void;
}

const DishCategoryIconUploader = ({ name, iconUrl, onUploaded }: Props) => {
  const [uploading, setUploading] = React.useState(false);

  const onImageSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const extension = file.name.split(".").pop();
    const timestamp = moment().format("DDMMYYYYHHmmss");
    const baseName = (name || "tag").replace(/\s/g, "");
    const fileName = `${baseName}_${timestamp}.${extension}`;

    setUploading(true);
    try {
      const links = await dishCategoryTagService.getAWSLinks(fileName);
      await dishCategoryTagService.uploadImageToAWS(links?.putUrl, file);
      onUploaded(links?.getUrl);
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: "Image uploaded successfully",
        type: CustomSnackbarTypes.SUCCESS,
      });
    } catch (err) {
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: "Failed to upload image",
        type: CustomSnackbarTypes.ERROR,
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        Icon Image
      </Typography>
      <input accept="image/*" type="file" onChange={onImageSelect} disabled={uploading} />

      <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
        {uploading && <CircularProgress size={20} />}
        {!uploading && iconUrl && (
          <Box
            component="img"
            src={iconUrl}
            alt="icon"
            sx={{
              width: 100,
              height: 100,
              objectFit: "contain",
              borderRadius: "8px",
              border: "1px solid rgba(0,0,0,0.1)",
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default DishCategoryIconUploader;
